(() => {
  // Stock map is populated from the JSON embedded in Liquid
  let STOCK_MAP = null;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function getFormId() {
    return $('[data-bundle-buy-buttons]')?.getAttribute('data-form-id')
      || $('[data-bundle-color-pickers]')?.getAttribute('data-form-id')
      || null;
  }

  /**
   * Parse a pack size from an option value string.
   * Handles "3-Pack", "3 Pack", "3x", "3", etc.
   */
  function parsePackSize(value) {
    if (!value) return 0;
    let v = String(value).trim();
    if (!v) return 0;
    v = v.split(' ')[0];
    if (v.includes('-')) v = v.split('-')[0];
    if (v.toLowerCase().endsWith('x')) v = v.slice(0, -1);
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /**
   * Get the selected value for an option by position (1-indexed).
   */
  function getSelectedOption(formId, optionPosition) {
    if (!formId) return null;
    const optionName = `option${optionPosition}`;

    const radio = $(`input[name="${optionName}"][form="${formId}"]:checked`);
    if (radio?.value) return radio.value;

    const select = $(`select[name="${optionName}"][form="${formId}"]`);
    if (select?.value) return select.value;

    const hidden = $(`input[name="${optionName}"][type="hidden"][form="${formId}"]`);
    if (hidden?.value) return hidden.value;

    const formEl = document.getElementById(formId);
    if (formEl) {
      const radioInForm = $(`input[name="${optionName}"]:checked`, formEl);
      if (radioInForm?.value) return radioInForm.value;
      const selectInForm = $(`select[name="${optionName}"]`, formEl);
      if (selectInForm?.value) return selectInForm.value;
      const hiddenInForm = $(`input[name="${optionName}"][type="hidden"]`, formEl);
      if (hiddenInForm?.value) return hiddenInForm.value;
    }

    const variantPicker = $(`variant-picker[form="${formId}"]`);
    if (variantPicker) {
      const radioInPicker = $(`input[name="${optionName}"]:checked`, variantPicker);
      if (radioInPicker?.value) return radioInPicker.value;
      const hiddenInPicker = $(`input[name="${optionName}"][type="hidden"]`, variantPicker);
      if (hiddenInPicker?.value) return hiddenInPicker.value;
    }

    const anyRadio = $(`input[name="${optionName}"]:checked`);
    if (anyRadio?.value) return anyRadio.value;
    const anyHidden = $(`input[name="${optionName}"][type="hidden"]`);
    if (anyHidden?.value) return anyHidden.value;

    return null;
  }

  // Pack Size is option1, Size is option2 (custom) or option3 (predefined)
  function getSelectedPackSize(formId) {
    const value = getSelectedOption(formId, 1);
    return parsePackSize(value);
  }

  function getMode() {
    const root = $('[data-bundle-color-pickers]');
    return root?.getAttribute('data-bundle-mode') || 'custom';
  }

  function getSizeOptionPosition() {
    const root = $('[data-bundle-color-pickers]');
    if (getMode() === 'predefined') {
      return parseInt(root?.getAttribute('data-shell-size-position') || '3', 10);
    }
    return 2;
  }

  function getSelectedSize(formId) {
    return getSelectedOption(formId, getSizeOptionPosition());
  }

  function handleize(str) {
    return String(str)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function resolveColorValue(colorValue, packSize) {
    if (!colorValue) return [];

    let parts;
    if (colorValue.includes('/')) {
      parts = colorValue.split('/').map(part => handleize(part));
    } else {
      parts = [handleize(colorValue)];
    }

    const count = packSize || parts.length;
    const result = [];
    for (let i = 0; i < count; i++) {
      result.push(parts[i % parts.length]);
    }
    return result;
  }

  function getSelectedColorValue(formId) {
    const root = $('[data-bundle-color-pickers]');
    if (!root || getMode() !== 'predefined') return null;
    const pos = parseInt(root.getAttribute('data-shell-color-position') || '2', 10);
    return getSelectedOption(formId, pos);
  }

  function getBundleProductTitle() {
    const titleEl = $('h1.product-title, .product-info h1, h1[class*="title"]');
    return titleEl ? titleEl.textContent.trim() : document.title.split('–')[0].trim();
  }

  /**
   * Load the stock map from the embedded JSON in Liquid.
   */
  function loadStockMap() {
    if (STOCK_MAP) return STOCK_MAP;

    const script = $('script[data-bundle-stock-map]');
    if (!script) {
      console.warn('[bundle-atc] No stock map found.');
      STOCK_MAP = {};
      return STOCK_MAP;
    }

    try {
      STOCK_MAP = JSON.parse(script.textContent);
    } catch (e) {
      console.error('[bundle-atc] Failed to parse stock map:', e);
      STOCK_MAP = {};
    }

    return STOCK_MAP;
  }

  /**
   * Normalize a size value to its common abbreviation.
   * "Small" → "S", "Medium" → "M", "Large" → "L", etc.
   */
  const SIZE_ALIASES = {
    'small': ['s', 'sm', 'sma'], 'medium': ['m', 'med'], 'large': ['l', 'lg', 'lrg'],
    'x-large': ['xl', 'xla', 'extra large', 'extra-large'],
    'extra small': ['xs', 'xsm', 'x-small', 'extra-small'],
    'extra extra small': ['xxs', '2xs'], 'extra extra large': ['xxl', '2xl', '2x'],
    'extra extra extra large': ['xxxl', '3xl', '3x'],
    's': ['small', 'sm', 'sma'], 'm': ['medium', 'med'], 'l': ['large', 'lg', 'lrg'],
    'xs': ['extra small', 'x-small', 'xsm'], 'sm': ['small', 's'],
    'xl': ['x-large', 'extra large', 'xla'], 'xxs': ['extra extra small', '2xs'],
    'xxl': ['extra extra large', '2xl', '2x'], '2xl': ['xxl', 'extra extra large', '2x'],
    '3xl': ['xxxl', 'extra extra extra large', '3x'],
    // Misentered spellings on some products (SMA/MED/LRG/XLA/XSM)
    'sma': ['s', 'small', 'sm'], 'med': ['m', 'medium'], 'lrg': ['l', 'large', 'lg'],
    'xla': ['xl', 'x-large', 'extra large'], 'xsm': ['xs', 'extra small', 'x-small'],
  };

  function getSizeAliases(size) {
    const norm = String(size).trim().toLowerCase();
    return [norm, ...(SIZE_ALIASES[norm] || [])];
  }

  /**
   * Get stock info for a color handle + size from the embedded map.
   * Returns { available: boolean, variantId: number } or null.
   */
  function getStockInfo(handle, size) {
    const map = loadStockMap();
    let color = map[handle];

    // Fuzzy handle match
    if (!color) color = map[handleize(handle)];
    if (!color) {
      for (const [key, val] of Object.entries(map)) {
        if (handleize(key) === handleize(handle)) { color = val; break; }
      }
    }

    if (!color || !color.sizes) return null;

    // Try exact match first
    if (color.sizes[size]) {
      return color.sizes[size];
    }

    // Try case-insensitive match
    const sizeNorm = String(size).trim().toLowerCase();
    for (const [key, val] of Object.entries(color.sizes)) {
      if (String(key).trim().toLowerCase() === sizeNorm) {
        return val;
      }
    }

    // Try size aliases ("Small" ↔ "S", "Medium" ↔ "M", etc.)
    const aliases = getSizeAliases(size);
    for (const alias of aliases) {
      for (const [key, val] of Object.entries(color.sizes)) {
        if (String(key).trim().toLowerCase() === alias) {
          return val;
        }
      }
    }

    return null;
  }

  function showError(msg) {
    const el = $('[data-bundle-atc-error]');
    if (!el) return;
    el.textContent = msg;
    el.style.display = 'block';
  }

  function clearError() {
    const el = $('[data-bundle-atc-error]');
    if (!el) return;
    el.textContent = '';
    el.style.display = 'none';
  }

  function getActiveSlots(root) {
    if (!root) return [];
    return $$('[data-bundle-slot]', root).filter(el => !el.hasAttribute('hidden') && !el.classList.contains('bundle-slot--hidden'));
  }

  function getRequiredSlots(root) {
    if (!root) return 0;
    const n = parseInt(root.dataset.requiredSlots || '', 10);
    if (Number.isFinite(n) && n > 0) return n;
    return getActiveSlots(root).length;
  }

  function getSelections() {
    const root = $('[data-bundle-color-pickers]');
    if (!root) return [];

    const mode = root.getAttribute('data-bundle-mode') || 'custom';

    if (mode === 'predefined') {
      const formId = getFormId();
      const colorValue = getSelectedColorValue(formId);
      const packSize = getSelectedPackSize(formId) || getRequiredSlots(root) || 3;
      return resolveColorValue(colorValue, packSize);
    }

    // Custom mode: get from individual slot selections
    const slotPicks = getActiveSlots(root).map(slot => {
      const sel = $('[data-bundle-swatch].is-selected', slot);
      return sel ? sel.getAttribute('data-handle') : null;
    });
    /* Hybrid shells (data-bundle-hybrid): the theme's native colour picker acts as
       slot 1 and the rendered slots are the extras, so prepend the native pick. */
    if (root.hasAttribute('data-bundle-hybrid')) {
      const nativeInput = document.querySelector('.variant-picker input[name="option1"]:checked, .variant-picker input[name="option2"]:checked');
      const nativeVal = nativeInput && /size/i.test(nativeInput.name) ? null : (nativeInput ? nativeInput.value : null);
      slotPicks.unshift(nativeVal ? handleize(nativeVal) : null);
    }
    return slotPicks;
  }

  async function addItems(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Add to cart failed');
    }

    return res.json();
  }

  function openCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { baseEvent: 'variant:add' } }));

    // Rebuy Smart Cart integration
    if (window.Rebuy && Rebuy.Cart && typeof Rebuy.Cart.fetch === 'function') {
      Rebuy.Cart.fetch().then(function() {
        if (Rebuy.SmartCart && typeof Rebuy.SmartCart.show === 'function') {
          Rebuy.SmartCart.show();
        }
      });
    }
  }

  /* ---- Wait & Save on hybrid shells ----
     The stock preorder flow swaps the form to a single shadow variant and adds
     N of it, which ignores the colour slots entirely. On hybrid shells build the
     same per-slot list Add to Cart builds, then map each base variant to its
     pre-order twin via the button's data-ws-map. Capture phase + stopPropagation
     so the button's own handler never runs. Non-hybrid PDPs are untouched. */
  document.addEventListener('click', async (e) => {
    const pre = e.target.closest('#AddToCart-Preorder, .preorder-button');
    if (!pre || !e.isTrusted) return;
    const root = $('[data-bundle-color-pickers]');
    if (!root || !root.hasAttribute('data-bundle-hybrid')) return;

    const mapEl = document.querySelector('[data-ws-map]');
    let wsMap = null;
    try { wsMap = JSON.parse(mapEl.getAttribute('data-ws-map')); } catch (err) { return; }
    if (!wsMap) return;

    e.preventDefault();
    e.stopPropagation();
    clearError();

    const formId = getFormId();
    const size = getSelectedSize(formId);
    if (!size) { showError('Select a size first.'); return; }

    const picks = getSelections();
    let required = getRequiredSlots(root);
    if (required) required += 1;
    if (!required || picks.length !== required || picks.some(p => !p)) {
      showError('Select all colors before adding to cart.');
      return;
    }

    pre.disabled = true;
    pre.setAttribute('aria-busy', 'true');

    const propInput = document.getElementById('pre-order');
    const preText = propInput ? propInput.value : 'Pre-Order';
    const bundleId = (Math.random().toString(36).slice(2) + Date.now().toString(36)).toUpperCase();
    const ups = parseInt(root.getAttribute('data-units-per-slot') || '1', 10) || 1;
    const packSize = String(picks.length * ups);
    const items = [];

    for (let i = 0; i < picks.length; i++) {
      const stockInfo = getStockInfo(picks[i], size);
      if (!stockInfo) { showError('One of your selections is unavailable in ' + size + '.'); pre.disabled = false; pre.removeAttribute('aria-busy'); return; }
      const preId = wsMap[String(stockInfo.variantId)];
      if (!preId) { showError('One of your colors is not available for pre-order.'); pre.disabled = false; pre.removeAttribute('aria-busy'); return; }
      items.push({
        id: preId,
        quantity: 1,
        properties: {
          'Pre-Order': preText,
          _bundle_id: bundleId,
          _bundle_type: 'mix-and-match-pack',
          _bundle_slot: String(i + 1),
          _bundle_size: String(size),
          _bundle_shell: window.location.pathname,
          _bundle_pack_size: packSize + '-Pack',
          _bundle_product: getBundleProductTitle()
        }
      });
    }

    /* fold identical colours into one line, then mirror page order */
    const seen = {}; const folded = [];
    items.forEach(function (it) {
      const k = String(it.id);
      if (seen[k]) { seen[k].quantity += it.quantity; seen[k].properties._bundle_slot += ',' + it.properties._bundle_slot; }
      else { seen[k] = it; folded.push(it); }
    });
    folded.reverse();

    try {
      await addItems(folded);
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true }));
    } catch (err) {
      showError('Could not add your pre-order. Please try again.');
    } finally {
      pre.disabled = false;
      pre.removeAttribute('aria-busy');
    }
  }, true);

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-bundle-atc]');
    if (!btn) return;
    /* Wait & Save submits by programmatically clicking this same button after
       swapping the variant to the pre-order shadow. Only intercept genuine user
       clicks: a scripted .click() reports isTrusted === false, so the preorder
       flow (and anything else that submits the form for us) passes through. */
    if (!e.isTrusted) return;
    if (window.__pvPreorderSubmit) return;

    e.preventDefault();
    clearError();

    const formId = getFormId();
    const size = getSelectedSize(formId);
    if (!size) {
      showError('Select a size first.');
      return;
    }

    const root = $('[data-bundle-color-pickers]');
    const mode = getMode();
    const picks = getSelections();

    if (mode === 'predefined') {
      // In predefined mode, picks come from the combo
      if (!picks.length) {
        showError('Select a color option before adding to cart.');
        return;
      }
    } else {
      // Custom mode: validate all slots filled
      let requiredSlots = getRequiredSlots(root);
      if (root && root.hasAttribute('data-bundle-hybrid')) requiredSlots += 1; // native picker is slot 1
      if (!requiredSlots || picks.length !== requiredSlots || picks.some(p => !p)) {
        showError('Select all colors before adding to cart.');
        return;
      }
    }

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');

    const bundleId = (Math.random().toString(36).slice(2) + Date.now().toString(36)).toUpperCase();
    const bundleTitle = getBundleProductTitle();
    const items = [];

    try {
      if (mode === 'predefined') {
        // Predefined mode: add BASE PRODUCT variants to cart.
        // "Black/White/Charcoal Grey/Light Blue" + 4-Pack → adds 4 individual base variants.
        // "Black" + 4-Pack → adds Black × 4.
        // "Black/White/Charcoal Grey/Light Blue" + 8-Pack → adds each × 2.

        const currentPackSize = getSelectedPackSize(formId) || picks.length;

        // Consolidate duplicate handles into { handle: qty }
        const handleQty = {};
        const handleOrder = [];
        for (const handle of picks) {
          if (!handleQty[handle]) {
            handleQty[handle] = 0;
            handleOrder.push(handle);
          }
          handleQty[handle]++;
        }

        for (let idx = 0; idx < handleOrder.length; idx++) {
          const handle = handleOrder[idx];
          const stockInfo = getStockInfo(handle, size);

          if (!stockInfo || !stockInfo.available) {
            const map = loadStockMap();
            const colorLabel = map[handle]?.label || handle;
            showError(`${colorLabel} is sold out in ${size}. Please choose another option.`);
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            return;
          }

          items.push({
            id: stockInfo.variantId,
            quantity: handleQty[handle],
            properties: {
              _bundle_id: bundleId,
              _bundle_type: 'curated-pack',
              _bundle_slot: String(idx + 1),
              _bundle_size: String(size),
              _bundle_shell: window.location.pathname,
              _bundle_pack_size: String(currentPackSize) + '-Pack',
              _bundle_product: bundleTitle
            }
          });
        }
      } else {
        // Custom mode: add individual base product variants, qty 1 each
        let packSize = getSelectedPackSize(formId) || `${getRequiredSlots(root)}`;
        if (root && root.hasAttribute('data-bundle-hybrid')) {
          const ups = parseInt(root.getAttribute('data-units-per-slot') || '1', 10) || 1;
          packSize = String(picks.length * ups);
        }

        for (let i = 0; i < picks.length; i++) {
          const handle = picks[i];
          const stockInfo = getStockInfo(handle, size);

          if (!stockInfo || !stockInfo.available) {
            showError(`One of your selections is sold out in ${size}. Please choose another color.`);
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            return;
          }

          items.push({
            id: stockInfo.variantId,
            quantity: 1,
            properties: {
              _bundle_id: bundleId,
              _bundle_type: 'mix-and-match-pack',
              _bundle_slot: String(i + 1),
              _bundle_size: String(size),
              _bundle_shell: window.location.pathname,
              _bundle_pack_size: String(packSize) + '-Pack',
              _bundle_product: bundleTitle
            }
          });
        }

        /* Hybrid shells: identical colour picks read better as one line with a
           quantity than as N one-unit lines. Shopify only merges lines whose
           properties match exactly, so fold duplicates here and join the slot
           numbers. Mixed colours are different variants and stay separate. */
        if (root && root.hasAttribute('data-bundle-hybrid') && items.length > 1) {
          const byVariant = {};
          const folded = [];
          items.forEach(function (it) {
            const key = String(it.id);
            if (byVariant[key]) {
              byVariant[key].quantity += it.quantity;
              byVariant[key].properties._bundle_slot += ',' + it.properties._bundle_slot;
            } else {
              byVariant[key] = it;
              folded.push(it);
            }
          });
          items.length = 0;
          folded.forEach(function (it) { items.push(it); });
        }
      }

      /* The cart drawer lists newest additions first, so posting in slot order
         puts the last slot on top. Reverse so the cart mirrors the page: slot 1
         (the native picker) first. Hybrid shells only; packs keep their order. */
      if (root && root.hasAttribute('data-bundle-hybrid')) items.reverse();

      await addItems(items);
      openCart();
    } catch (err) {
      showError(err?.message || 'Add to cart failed.');
    } finally {
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });
})();

